import { Parameter } from '@shared/types';

export type Level = 'beginner' | 'intermediate' | 'expert';
export type DataType =
  | 'number'
  | 'string'
  | 'boolean'
  | 'array'
  | 'range'
  | 'special';
export type UiSize = 'compact' | 'large';
export type Origin = 'native' | 'meshpit';

/**
 * Marks a substring of the snippet for tooltip annotation.
 * `text` must appear verbatim in the snippet (matched left-to-right,
 * each occurrence consumed once).
 */
export type SnippetAnnotation = {
  text: string;
  tooltip: string;
};

export type CheatsheetEntry = {
  id: string;
  title: string;
  level: Level;
  dataType: DataType;
  uiSize: UiSize;
  origin: Origin;
  description: string;
  snippet: string;
  /** Optional per-token tooltip annotations for the code snippet. */
  annotations?: SnippetAnnotation[];
  param: Parameter;
  blogSlug?: string;
  blogAnchor?: string;
};

export const CHEATSHEET_ENTRIES: CheatsheetEntry[] = [
  // ─── BEGINNER / NATIVE ──────────────────────────────────────────────────

  {
    id: 'number-slider',
    title: 'Number Slider',
    level: 'beginner',
    dataType: 'number',
    uiSize: 'compact',
    origin: 'native',
    description:
      'Any numeric variable becomes a slider automatically. The range is auto-calculated from the default value.',
    snippet: 'height = 42;',
    annotations: [
      {
        text: 'height',
        tooltip: 'Variable name — becomes the parameter label',
      },
      {
        text: '42',
        tooltip: 'Default value — also the initial slider position',
      },
    ],
    param: {
      name: 'height',
      displayName: 'Height',
      value: 42,
      defaultValue: 42,
      type: 'number',
      group: 'General',
    },
    blogSlug: 'advanced-parameters',
  },

  {
    id: 'number-slider-range',
    title: 'Slider with Range',
    level: 'beginner',
    dataType: 'number',
    uiSize: 'compact',
    origin: 'native',
    description: 'Add a `[min:max]` comment to constrain the slider range.',
    snippet: 'height = 42; // [5:200]',
    annotations: [
      {
        text: 'height',
        tooltip: 'Variable name — becomes the parameter label',
      },
      { text: '42', tooltip: 'Default value' },
      { text: '5', tooltip: 'Minimum — slider left bound' },
      { text: '200', tooltip: 'Maximum — slider right bound' },
    ],
    param: {
      name: 'height',
      displayName: 'Height',
      value: 42,
      defaultValue: 42,
      type: 'number',
      range: { min: 5, max: 200 },
      group: 'General',
    },
    blogSlug: 'advanced-parameters',
  },

  {
    id: 'number-slider-step',
    title: 'Slider with Step',
    level: 'beginner',
    dataType: 'number',
    uiSize: 'compact',
    origin: 'native',
    description:
      'Use `[min:step:max]` to snap the slider to discrete increments.',
    snippet: 'wall_thickness = 1.6; // [0.4:0.4:4]',
    annotations: [
      {
        text: 'wall_thickness',
        tooltip: 'Variable name — becomes the parameter label',
      },
      { text: '1.6', tooltip: 'Default value' },
      { text: '0.4', tooltip: 'Minimum — slider left bound' },
      {
        text: '0.4',
        tooltip: 'Step — slider snaps to multiples of this value',
      },
      { text: '4', tooltip: 'Maximum — slider right bound' },
    ],
    param: {
      name: 'wall_thickness',
      displayName: 'Wall Thickness',
      value: 1.6,
      defaultValue: 1.6,
      type: 'number',
      range: { min: 0.4, step: 0.4, max: 4 },
      group: 'General',
    },
    blogSlug: 'advanced-parameters',
  },

  {
    id: 'number-unit-label',
    title: 'Unit Label',
    level: 'intermediate',
    dataType: 'number',
    uiSize: 'compact',
    origin: 'meshpit',
    description:
      'Add `unit=<label>` to the inline comment to display an arbitrary unit after the number input. Works on scalar numbers and `number[]` vectors. Use `unit=` (empty) to explicitly suppress the automatic "mm" heuristic.',
    snippet:
      'fan_speed = 50; // [0:100] unit=%\nwall = 2.0; // [0.4:0.2:8] unit=mm\nscale = 1.0; // [0.1:0.1:5] unit=',
    annotations: [
      { text: 'fan_speed', tooltip: 'Variable name — becomes the parameter label' },
      { text: '[0:100]', tooltip: 'Slider range' },
      { text: 'unit=%', tooltip: 'Explicit unit label — "%" is shown after the number input' },
      { text: 'wall', tooltip: 'Variable name whose name would trigger the "mm" heuristic' },
      { text: '[0.4:0.2:8]', tooltip: 'Slider range with step' },
      { text: 'unit=mm', tooltip: 'Explicit "mm" label — same result as the name-based heuristic, but explicit' },
      { text: 'scale', tooltip: 'Variable name with no measurement keyword' },
      { text: '[0.1:0.1:5]', tooltip: 'Slider range' },
      { text: 'unit=', tooltip: 'Empty unit — suppresses the automatic "mm" heuristic even if the name matches' },
    ],
    param: {
      name: 'fan_speed',
      displayName: 'Fan Speed',
      value: 50,
      defaultValue: 50,
      type: 'number',
      range: { min: 0, max: 100 },
      unit: '%',
      group: 'General',
    },
  },

  {
    id: 'number-slider-extended-range',
    title: 'Slider with Extended Range',
    level: 'intermediate',
    dataType: 'number',
    uiSize: 'compact',
    origin: 'native',
    description:
      'Use `[outerMin .. [safeMin:step:safeMax] .. outerMax]` to define a recommended "safe" range inside a wider absolute range. The slider is constrained to the safe zone by default; the outer bounds are reachable by typing directly into the number field.',
    snippet: 'width = 88; // [5 .. [20:1:120] .. 150]',
    annotations: [
      { text: 'width', tooltip: 'Variable name — becomes the parameter label' },
      { text: '88', tooltip: 'Default value' },
      {
        text: '5',
        tooltip: 'Absolute minimum — reachable by typing, not by dragging',
      },
      { text: '20', tooltip: 'Safe minimum — slider left bound' },
      { text: '1', tooltip: 'Step — slider snaps to multiples of this value' },
      { text: '120', tooltip: 'Safe maximum — slider right bound' },
      {
        text: '150',
        tooltip: 'Absolute maximum — reachable by typing, not by dragging',
      },
    ],
    param: {
      name: 'width',
      displayName: 'Width',
      value: 88,
      defaultValue: 88,
      type: 'number',
      range: { min: 5, safeMin: 20, step: 1, safeMax: 120, max: 150 },
      group: 'General',
    },
    blogSlug: 'advanced-parameters',
  },

  {
    id: 'boolean-toggle',
    title: 'Boolean Toggle',
    level: 'beginner',
    dataType: 'boolean',
    uiSize: 'compact',
    origin: 'native',
    description: '`true` or `false` values render as a toggle switch.',
    snippet: 'add_lid = true;',
    annotations: [
      {
        text: 'add_lid',
        tooltip: 'Variable name — becomes the parameter label',
      },
      { text: 'true', tooltip: 'Default value — toggle starts on' },
    ],
    param: {
      name: 'add_lid',
      displayName: 'Add Lid',
      value: true,
      defaultValue: true,
      type: 'boolean',
      group: 'General',
    },
    blogSlug: 'advanced-parameters',
  },

  {
    id: 'string-input',
    title: 'Text Input',
    level: 'beginner',
    dataType: 'string',
    uiSize: 'compact',
    origin: 'native',
    description:
      'Quoted string variables with no options become a text input field.',
    snippet: 'label_text = "Hello World";',
    annotations: [
      {
        text: 'label_text',
        tooltip: 'Variable name — becomes the parameter label',
      },
      {
        text: '"Hello World"',
        tooltip: 'Default string value — shown in the text field',
      },
    ],
    param: {
      name: 'label_text',
      displayName: 'Label Text',
      value: 'Hello World',
      defaultValue: 'Hello World',
      type: 'string',
      group: 'General',
    },
    blogSlug: 'advanced-parameters',
  },

  {
    id: 'number-dropdown',
    title: 'Number Dropdown',
    level: 'beginner',
    dataType: 'number',
    uiSize: 'compact',
    origin: 'native',
    description:
      'Add `[opt1, opt2, ...]` to a number parameter for a toggle group or dropdown.',
    snippet: 'sides = 6; // [3, 4, 6, 8]',
    annotations: [
      { text: 'sides', tooltip: 'Variable name — becomes the parameter label' },
      { text: '6', tooltip: 'Default value — initially selected option' },
      {
        text: '[3, 4, 6, 8]',
        tooltip: 'Option list — rendered as a toggle group or dropdown',
      },
    ],
    param: {
      name: 'sides',
      displayName: 'Sides',
      value: 6,
      defaultValue: 6,
      type: 'number',
      options: [
        { value: 3, label: '3' },
        { value: 4, label: '4' },
        { value: 6, label: '6' },
        { value: 8, label: '8' },
      ],
      group: 'General',
    },
    blogSlug: 'advanced-parameters',
  },

  {
    id: 'labelled-dropdown',
    title: 'Labelled Dropdown',
    level: 'beginner',
    dataType: 'number',
    uiSize: 'compact',
    origin: 'native',
    description:
      'Use `value=Label` pairs to give human-readable names to numeric options.',
    snippet: 'quality = 32; // 16=Draft, 32=Normal, 64=Fine',
    annotations: [
      {
        text: 'quality',
        tooltip: 'Variable name — becomes the parameter label',
      },
      { text: '32', tooltip: 'Default value — "Normal" is initially selected' },
      { text: '16=Draft', tooltip: 'Numeric value 16 shown as "Draft"' },
      { text: '32=Normal', tooltip: 'Numeric value 32 shown as "Normal"' },
      { text: '64=Fine', tooltip: 'Numeric value 64 shown as "Fine"' },
    ],
    param: {
      name: 'quality',
      displayName: 'Quality',
      value: 32,
      defaultValue: 32,
      type: 'number',
      options: [
        { value: 16, label: 'Draft' },
        { value: 32, label: 'Normal' },
        { value: 64, label: 'Fine' },
      ],
      group: 'General',
    },
    blogSlug: 'advanced-parameters',
  },

  {
    id: 'labelled-dropdown-bracket',
    title: 'Labelled Dropdown (Bracket)',
    level: 'beginner',
    dataType: 'number',
    uiSize: 'compact',
    origin: 'native',
    description:
      'OpenSCAD\'s native bracket syntax: `[value:Label, ...]` gives human-readable names to numeric options.',
    snippet: 'quality = 32; // [16:Draft, 32:Normal, 64:Fine]',
    annotations: [
      {
        text: 'quality',
        tooltip: 'Variable name — becomes the parameter label',
      },
      { text: '32', tooltip: 'Default value — "Normal" is initially selected' },
      {
        text: '[16:Draft, 32:Normal, 64:Fine]',
        tooltip: 'Bracket list of value:Label pairs — OpenSCAD native syntax',
      },
    ],
    param: {
      name: 'quality',
      displayName: 'Quality',
      value: 32,
      defaultValue: 32,
      type: 'number',
      options: [
        { value: 16, label: 'Draft' },
        { value: 32, label: 'Normal' },
        { value: 64, label: 'Fine' },
      ],
      group: 'General',
    },
    blogSlug: 'advanced-parameters',
  },

  {
    id: 'string-dropdown',
    title: 'String Dropdown',
    level: 'beginner',
    dataType: 'string',
    uiSize: 'compact',
    origin: 'native',
    description:
      'Add `[opt1, opt2, ...]` to a string parameter for a toggle group or dropdown.',
    snippet: 'style = "round"; // [round, square, hex]',
    annotations: [
      { text: 'style', tooltip: 'Variable name — becomes the parameter label' },
      { text: '"round"', tooltip: 'Default value — initially selected option' },
      {
        text: '[round, square, hex]',
        tooltip: 'String option list — rendered as a toggle group or dropdown',
      },
    ],
    param: {
      name: 'style',
      displayName: 'Style',
      value: 'round',
      defaultValue: 'round',
      type: 'string',
      options: [
        { value: 'round', label: 'round' },
        { value: 'square', label: 'square' },
        { value: 'hex', label: 'hex' },
      ],
      group: 'General',
    },
    blogSlug: 'advanced-parameters',
  },

  {
    id: 'color-picker',
    title: 'Color Picker',
    level: 'beginner',
    dataType: 'array',
    uiSize: 'compact',
    origin: 'native',
    description:
      'A `[R, G, B]` array whose variable name contains "color" renders as a full color picker.',
    snippet: 'body_color = [0.2, 0.6, 1.0];',
    annotations: [
      {
        text: 'body_color',
        tooltip: 'Name contains "color" — triggers the color picker widget',
      },
      { text: '0.2', tooltip: 'Red channel (0–1)' },
      { text: '0.6', tooltip: 'Green channel (0–1)' },
      { text: '1.0', tooltip: 'Blue channel (0–1)' },
    ],
    param: {
      name: 'body_color',
      displayName: 'Body Color',
      value: [0.2, 0.6, 1.0],
      defaultValue: [0.2, 0.6, 1.0],
      type: 'color',
      group: 'General',
    },
    blogSlug: 'advanced-parameters',
  },

  {
    id: 'color-picker-alpha',
    title: 'Color Picker (with Alpha)',
    level: 'intermediate',
    dataType: 'array',
    uiSize: 'compact',
    origin: 'native',
    description:
      'A `[R, G, B, A]` array adds an opacity slider below the color picker.',
    snippet: 'glass_color = [0.8, 0.9, 1.0, 0.4];',
    annotations: [
      {
        text: 'glass_color',
        tooltip: 'Name contains "color" — triggers the color picker widget',
      },
      { text: '0.8', tooltip: 'Red channel (0–1)' },
      { text: '0.9', tooltip: 'Green channel (0–1)' },
      { text: '1.0', tooltip: 'Blue channel (0–1)' },
      {
        text: '0.4',
        tooltip: 'Alpha / opacity (0–1) — adds an opacity slider',
      },
    ],
    param: {
      name: 'glass_color',
      displayName: 'Glass Color',
      value: [0.8, 0.9, 1.0, 0.4],
      defaultValue: [0.8, 0.9, 1.0, 0.4],
      type: 'color',
      group: 'General',
    },
    blogSlug: 'advanced-parameters',
  },

  {
    id: 'vector-sliders',
    title: 'Vector Sliders',
    level: 'intermediate',
    dataType: 'array',
    uiSize: 'compact',
    origin: 'native',
    description:
      'A numeric array with a `[label1, label2, ...]` comment becomes labelled sliders.',
    snippet: 'offset = [10, 5, 0]; // [x, y, z]',
    annotations: [
      {
        text: 'offset',
        tooltip: 'Variable name — becomes the parameter label',
      },
      { text: '10', tooltip: 'Default X value' },
      { text: '5', tooltip: 'Default Y value' },
      { text: '0', tooltip: 'Default Z value' },
      {
        text: '[x, y, z]',
        tooltip:
          'Axis labels — each array element gets its own labelled slider',
      },
    ],
    param: {
      name: 'offset',
      displayName: 'Offset',
      value: [10, 5, 0],
      defaultValue: [10, 5, 0],
      type: 'number[]',
      vectorLabels: ['x', 'y', 'z'],
      group: 'General',
    },
    blogSlug: 'advanced-parameters',
  },

  {
    id: 'range-input',
    title: 'Range Input',
    level: 'intermediate',
    dataType: 'range',
    uiSize: 'compact',
    origin: 'native',
    description:
      'An OpenSCAD range literal `[start:step:end]` becomes Start / Step / End number fields.',
    snippet: 'helix_range = [0:5:360];',
    annotations: [
      {
        text: 'helix_range',
        tooltip: 'Variable name — becomes the parameter label',
      },
      { text: '0', tooltip: 'Start value' },
      {
        text: '5',
        tooltip: 'Step — increment between each value in the range',
      },
      { text: '360', tooltip: 'End value' },
    ],
    param: {
      name: 'helix_range',
      displayName: 'Helix Range',
      value: { start: 0, step: 5, end: 360 },
      defaultValue: { start: 0, step: 5, end: 360 },
      type: 'range',
      group: 'General',
    },
    blogSlug: 'advanced-parameters',
  },

  {
    id: 'parameter-groups',
    title: 'Parameter Groups',
    level: 'beginner',
    dataType: 'special',
    uiSize: 'compact',
    origin: 'native',
    description:
      'Use `/* [Group Name] */` comments to organize parameters into collapsible accordion sections.',
    snippet:
      '/* [Dimensions] */\nwidth = 40;\nheight = 20;\n\n/* [Options] */\nadd_lid = true;',
    annotations: [
      {
        text: '/* [Dimensions] */',
        tooltip:
          'Group header — all variables below belong to the "Dimensions" accordion section',
      },
      {
        text: '/* [Options] */',
        tooltip: 'Group header — starts a new "Options" section',
      },
    ],
    param: {
      name: 'width',
      displayName: 'Width',
      value: 40,
      defaultValue: 40,
      type: 'number',
      group: 'Dimensions',
    },
    blogSlug: 'advanced-parameters',
  },

  {
    id: 'tab-groups',
    title: 'Tab Groups',
    level: 'intermediate',
    dataType: 'special',
    uiSize: 'compact',
    origin: 'native',
    description:
      'Use `/* [Tab: Group] */` to organize groups under named tabs in the parameter panel.',
    snippet:
      '/* [Design: Shape] */\nshape = "round"; // [round, square]\n\n/* [Design: Size] */\nwidth = 40;',
    annotations: [
      {
        text: 'Design',
        tooltip: 'Tab name — groups are nested under a tab with this label',
      },
      { text: 'Shape', tooltip: 'Group name within the tab' },
      { text: 'Size', tooltip: 'Another group in the same tab' },
    ],
    param: {
      name: 'shape',
      displayName: 'Shape',
      value: 'round',
      defaultValue: 'round',
      type: 'string',
      options: [
        { value: 'round', label: 'round' },
        { value: 'square', label: 'square' },
      ],
      group: 'Shape',
      tab: 'Design',
    },
    blogSlug: 'advanced-parameters',
  },

  {
    id: 'stop-parsing',
    title: 'Stop Parsing',
    level: 'beginner',
    dataType: 'special',
    uiSize: 'compact',
    origin: 'native',
    description:
      'Add `// --- parameters end ---` to stop the parser — variables below this line are ignored by the parameter panel.',
    snippet:
      'height = 42;\n// --- parameters end ---\n// Internal computed values below:',
    annotations: [
      {
        text: '// --- parameters end ---',
        tooltip:
          'Sentinel comment — the parser stops here; anything below is invisible to the parameter panel',
      },
    ],
    param: {
      name: 'height',
      displayName: 'Height',
      value: 42,
      defaultValue: 42,
      type: 'number',
      group: 'General',
    },
    blogSlug: 'advanced-parameters',
  },

  // ─── INTERMEDIATE / MESHPIT ─────────────────────────────────────────────

  {
    id: 'angle-dial',
    title: 'Angle Dial',
    level: 'intermediate',
    dataType: 'number',
    uiSize: 'compact',
    origin: 'meshpit',
    description:
      'Add a degree symbol `°` to the range comment to get a circular arc dial.',
    snippet: 'rotation = 0; // [-180°:180°]',
    annotations: [
      {
        text: 'rotation',
        tooltip: 'Variable name — becomes the parameter label',
      },
      { text: '0', tooltip: 'Default angle value' },
      {
        text: '-180°',
        tooltip: 'Minimum angle — the ° symbol triggers the dial widget',
      },
      { text: '180°', tooltip: 'Maximum angle' },
    ],
    param: {
      name: 'rotation',
      displayName: 'Rotation',
      value: 0,
      defaultValue: 0,
      type: 'angle',
      range: { min: -180, max: 180 },
      group: 'General',
    },
    blogSlug: 'stepper-and-xypad',
  },

  {
    id: 'stepper',
    title: 'Stepper',
    level: 'intermediate',
    dataType: 'number',
    uiSize: 'compact',
    origin: 'meshpit',
    description:
      'Use `<val:Label, val:Label>` syntax to create prev/next arrow buttons for stepping through named values.',
    snippet: 'resolution = 32; // <16:Draft, 32:Normal, 64:Fine>',
    annotations: [
      {
        text: 'resolution',
        tooltip: 'Variable name — becomes the parameter label',
      },
      { text: '32', tooltip: 'Default value — "Normal" is the initial step' },
      {
        text: '<16:Draft, 32:Normal, 64:Fine>',
        tooltip:
          'Stepper definition — angle brackets trigger the stepper widget; each entry is value:Label',
      },
      { text: '16:Draft', tooltip: 'Value 16 is labelled "Draft"' },
      { text: '32:Normal', tooltip: 'Value 32 is labelled "Normal"' },
      { text: '64:Fine', tooltip: 'Value 64 is labelled "Fine"' },
    ],
    param: {
      name: 'resolution',
      displayName: 'Resolution',
      value: 32,
      defaultValue: 32,
      type: 'stepper',
      options: [
        { value: 16, label: 'Draft' },
        { value: 32, label: 'Normal' },
        { value: 64, label: 'Fine' },
      ],
      group: 'General',
    },
    blogSlug: 'stepper-and-xypad',
  },

  {
    id: 'xy-pad',
    title: 'XY Pad',
    level: 'intermediate',
    dataType: 'array',
    uiSize: 'large',
    origin: 'meshpit',
    description:
      'A two-element array with two range specs `[min:max, min:max]` in the comment becomes a draggable 2D canvas.',
    snippet: 'position = [0, 0]; // [-50:50, -50:50]',
    annotations: [
      {
        text: 'position',
        tooltip: 'Variable name — becomes the parameter label',
      },
      { text: '[0, 0]', tooltip: 'Default [x, y] values' },
      { text: '-50:50', tooltip: 'X axis range: min –50, max 50' },
      { text: '-50:50', tooltip: 'Y axis range: min –50, max 50' },
    ],
    param: {
      name: 'position',
      displayName: 'Position',
      value: [0, 0],
      defaultValue: [0, 0],
      type: 'xypad',
      xyPadAxes: {
        x: { min: -50, max: 50 },
        y: { min: -50, max: 50 },
      },
      group: 'General',
    },
    blogSlug: 'stepper-and-xypad',
  },

  {
    id: 'xyz-pad',
    title: 'XYZ Pad',
    level: 'intermediate',
    dataType: 'array',
    uiSize: 'large',
    origin: 'meshpit',
    description:
      'A three-element array with three range specs becomes an XY canvas plus a Z slider.',
    snippet: 'position = [0, 0, 0]; // [-50:50, -50:50, 0:100]',
    annotations: [
      {
        text: 'position',
        tooltip: 'Variable name — becomes the parameter label',
      },
      { text: '[0, 0, 0]', tooltip: 'Default [x, y, z] values' },
      { text: '-50:50', tooltip: 'X axis range: min –50, max 50' },
      { text: '-50:50', tooltip: 'Y axis range: min –50, max 50' },
      {
        text: '0:100',
        tooltip: 'Z axis range: min 0, max 100 — rendered as a separate slider',
      },
    ],
    param: {
      name: 'position',
      displayName: 'Position',
      value: [0, 0, 0],
      defaultValue: [0, 0, 0],
      type: 'xyzpad',
      xyzPadAxes: {
        x: { min: -50, max: 50 },
        y: { min: -50, max: 50 },
        z: { min: 0, max: 100 },
      },
      group: 'General',
    },
    blogSlug: 'stepper-and-xypad',
  },

  {
    id: 'color-swatches',
    title: 'Color Swatches',
    level: 'intermediate',
    dataType: 'string',
    uiSize: 'compact',
    origin: 'meshpit',
    description:
      'A color parameter with a `[#hex1, #hex2, ...]` options list renders as clickable color swatches.',
    snippet:
      'accent_color = "#3b82f6"; // [#ef4444, #3b82f6, #22c55e, #f59e0b]',
    annotations: [
      {
        text: 'accent_color',
        tooltip: 'Name contains "color" — triggers color widget',
      },
      { text: '"#3b82f6"', tooltip: 'Default color (blue)' },
      { text: '#ef4444', tooltip: 'Swatch option: red' },
      { text: '#3b82f6', tooltip: 'Swatch option: blue (default)' },
      { text: '#22c55e', tooltip: 'Swatch option: green' },
      { text: '#f59e0b', tooltip: 'Swatch option: amber' },
    ],
    param: {
      name: 'accent_color',
      displayName: 'Accent Color',
      value: '#3b82f6',
      defaultValue: '#3b82f6',
      type: 'color',
      options: [
        { value: '#ef4444', label: '#ef4444' },
        { value: '#3b82f6', label: '#3b82f6' },
        { value: '#22c55e', label: '#22c55e' },
        { value: '#f59e0b', label: '#f59e0b' },
      ],
      group: 'General',
    },
    blogSlug: 'advanced-parameters',
  },

  {
    id: 'font-picker',
    title: 'Font Picker',
    level: 'intermediate',
    dataType: 'string',
    uiSize: 'compact',
    origin: 'meshpit',
    description:
      'A string variable whose name contains "font" renders as a font browser showing all OpenSCAD fonts.',
    snippet: 'label_font = "Liberation Sans";',
    annotations: [
      {
        text: 'label_font',
        tooltip: 'Name contains "font" — triggers the font picker widget',
      },
      {
        text: '"Liberation Sans"',
        tooltip: 'Default font name — must be a valid OpenSCAD font',
      },
    ],
    param: {
      name: 'label_font',
      displayName: 'Label Font',
      value: 'Liberation Sans',
      defaultValue: 'Liberation Sans',
      type: 'font',
      group: 'General',
    },
    blogSlug: 'advanced-parameters',
  },

  {
    id: 'file-upload',
    title: 'File Upload',
    level: 'intermediate',
    dataType: 'string',
    uiSize: 'compact',
    origin: 'meshpit',
    description:
      'Precede a string variable with `// upload` to render a file picker button for attaching external files.',
    snippet: '// upload\nsvg_file = "logo.svg";',
    annotations: [
      {
        text: '// upload',
        tooltip:
          'Directive — next string variable becomes a file upload button',
      },
      {
        text: 'svg_file',
        tooltip: 'Variable name — holds the uploaded filename',
      },
      {
        text: '"logo.svg"',
        tooltip:
          'Default filename — shown on the upload button before a file is chosen',
      },
    ],
    param: {
      name: 'svg_file',
      displayName: 'Svg File',
      value: 'logo.svg',
      defaultValue: 'logo.svg',
      type: 'file',
      group: 'General',
    },
    blogSlug: 'advanced-parameters',
  },

  {
    id: 'markdown-block',
    title: 'Markdown Block',
    level: 'intermediate',
    dataType: 'special',
    uiSize: 'compact',
    origin: 'meshpit',
    description:
      'Use `// markdown` or `// markdown:variant` followed by `//` lines to embed a rich-text callout in the parameter panel. Variants: `tip`, `note`, `warning`, `idea`, `error`.',
    snippet:
      '// markdown:tip\n// Use **bold** or _italic_ text.\n// [Open blog](meshpit:snapshot:default)',
    annotations: [
      {
        text: '// markdown:tip',
        tooltip:
          'Starts a markdown block with the "tip" variant (green sparkle callout). Other variants: note, warning, idea, error',
      },
      { text: '**bold**', tooltip: 'Markdown bold syntax' },
      { text: '_italic_', tooltip: 'Markdown italic syntax' },
      {
        text: 'meshpit:snapshot:default',
        tooltip: 'Action link — clicking applies the named snapshot',
      },
    ],
    param: {
      name: '__markdown_0',
      displayName: '',
      value: 'Use **bold** or _italic_ text in your callouts.',
      defaultValue: 'Use **bold** or _italic_ text in your callouts.',
      type: 'markdown',
      markdownVariant: 'tip',
      group: 'General',
    },
    blogSlug: 'smart-parameters',
  },

  {
    id: 'grid-input',
    title: 'Grid Input',
    level: 'intermediate',
    dataType: 'array',
    uiSize: 'large',
    origin: 'meshpit',
    description:
      'A 2D array with a `// grid: CxR [opt1, opt2, ...]` comment renders as a clickable grid. Options can span multiple cells with a `:WxH` suffix — covered cells store the reserved sentinel `"span"`.',
    snippet:
      'layout = [["big","span","small"],["span","span","empty"]]; // grid: 3x2 [empty:1x1, small#4488ff:1x1, big#ff8844:2x2]',
    annotations: [
      {
        text: 'layout',
        tooltip: 'Variable name — holds the grid state as a 2D array',
      },
      {
        text: '"span"',
        tooltip:
          'Reserved sentinel — written into cells covered by a spanning tile; SCAD code skips these with `if (part != "span")`',
      },
      { text: '3x2', tooltip: 'Grid dimensions: 3 columns × 2 rows' },
      {
        text: 'empty:1x1',
        tooltip: 'Option with explicit 1×1 span (the default — can be omitted)',
      },
      {
        text: 'small#4488ff:1x1',
        tooltip: 'Option with a custom hex colour (#4488ff) and 1×1 span',
      },
      {
        text: 'big#ff8844:2x2',
        tooltip:
          'Option with a custom hex colour and a 2×2 span — occupies 4 cells; the popover only offers this option when its full footprint is free',
      },
    ],
    param: {
      name: 'layout',
      displayName: 'Layout',
      value: ['big', 'span', 'small', 'span', 'span', 'empty'],
      defaultValue: ['big', 'span', 'small', 'span', 'span', 'empty'],
      type: 'grid',
      gridConfig: {
        cols: 3,
        rows: 2,
        options: ['empty', 'small', 'big'],
        optionColours: { small: '#4488ff', big: '#ff8844' },
        optionSpans: { big: [2, 2] },
      },
      group: 'General',
    },
    blogSlug: 'grid-api',
  },

  {
    id: 'stack-input',
    title: 'Stack Input',
    level: 'intermediate',
    dataType: 'array',
    uiSize: 'large',
    origin: 'meshpit',
    description:
      'A 1D array with a `// stack: min:max [opt1, opt2, ...]` comment renders as a dynamic reorderable list. Users can add, remove, and drag-reorder items, constrained to the allowed options and optional min/max length bounds. Options can carry a `#hex` colour suffix.',
    snippet:
      'layout = ["pot","empty","jack"]; // stack: 1:10 [pot, jack, empty]',
    annotations: [
      {
        text: 'layout',
        tooltip: 'Variable name — holds the stack as a 1D string array',
      },
      {
        text: '1:10',
        tooltip:
          'Min:Max length bounds — list cannot be shorter than 1 or longer than 10. Both are optional; omit entirely for an unbounded list.',
      },
      {
        text: 'pot',
        tooltip:
          'Allowed option — can carry a #hex colour suffix, e.g. pot#ff3300',
      },
      {
        text: 'jack',
        tooltip: 'Allowed option',
      },
      {
        text: 'empty',
        tooltip:
          'Allowed option — names containing "empty", "none", or "blank" get a muted grey colour automatically',
      },
    ],
    param: {
      name: 'layout',
      displayName: 'Layout',
      value: ['pot', 'empty', 'jack'],
      defaultValue: ['pot', 'empty', 'jack'],
      type: 'stack',
      stackConfig: {
        min: 1,
        max: 10,
        options: ['pot', 'jack', 'empty'],
      },
      group: 'General',
    },
  },

  // ─── EXPERT / MESHPIT ───────────────────────────────────────────────────

  {
    id: 'polygon-pad',
    title: 'Polygon Pad',
    level: 'expert',
    dataType: 'array',
    uiSize: 'large',
    origin: 'meshpit',
    description:
      'A nested array of `[x, y]` pairs becomes an interactive polygon editor where you can drag vertices on a 2D canvas.',
    snippet: 'outline = [[0,0], [40,0], [40,30], [0,30]];',
    annotations: [
      {
        text: 'outline',
        tooltip: 'Variable name — becomes the parameter label',
      },
      { text: '[0,0]', tooltip: 'Vertex 1: x=0, y=0' },
      { text: '[40,0]', tooltip: 'Vertex 2: x=40, y=0' },
      { text: '[40,30]', tooltip: 'Vertex 3: x=40, y=30' },
      { text: '[0,30]', tooltip: 'Vertex 4: x=0, y=30' },
    ],
    param: {
      name: 'outline',
      displayName: 'Outline',
      value: [0, 0, 40, 0, 40, 30, 0, 30],
      defaultValue: [0, 0, 40, 0, 40, 30, 0, 30],
      type: 'polygonpad',
      polygonPadConfig: {
        pointConstraints: [
          [
            { kind: 'free', defaultValue: 0 },
            { kind: 'free', defaultValue: 0 },
          ],
          [
            { kind: 'free', defaultValue: 40 },
            { kind: 'free', defaultValue: 0 },
          ],
          [
            { kind: 'free', defaultValue: 40 },
            { kind: 'free', defaultValue: 30 },
          ],
          [
            { kind: 'free', defaultValue: 0 },
            { kind: 'free', defaultValue: 30 },
          ],
        ],
      },
      group: 'General',
    },
    blogSlug: 'polygon-pad',
  },

  {
    id: 'svg-checkbox',
    title: 'SVG Checkbox Group',
    level: 'expert',
    dataType: 'array',
    uiSize: 'large',
    origin: 'meshpit',
    description:
      'Precede a string with `// svgcheckboxgroup` to create a multi-select visual toggle using SVG path groups as buttons.',
    snippet:
      '// svgcheckboxgroup\nparts = "enclosure.svg"; // [base, case, lid]',
    annotations: [
      {
        text: '// svgcheckboxgroup',
        tooltip:
          'Directive — next string variable becomes a multi-select SVG group picker',
      },
      {
        text: 'parts',
        tooltip: 'Variable name — holds the array of selected group IDs',
      },
      {
        text: '"enclosure.svg"',
        tooltip:
          'SVG file to render as the selector — group IDs must match the options list',
      },
      {
        text: '[base, case, lid]',
        tooltip: 'SVG group IDs to expose as toggleable regions',
      },
    ],
    param: {
      name: 'parts',
      displayName: 'Parts',
      value: ['base', 'lid'],
      defaultValue: ['base', 'lid'],
      type: 'svgcheckboxgroup',
      options: [
        { value: 'base', label: 'base' },
        { value: 'case', label: 'case' },
        { value: 'lid', label: 'lid' },
      ],
      svgFile: 'enclosure.svg',
      group: 'General',
    },
    blogSlug: 'svg-interactive-parameters',
  },

  {
    id: 'svg-radio',
    title: 'SVG Radio Group',
    level: 'expert',
    dataType: 'string',
    uiSize: 'large',
    origin: 'meshpit',
    description:
      'Precede a string with `// svgradiogroup` to create a single-select visual picker using SVG path groups.',
    snippet:
      '// svgradiogroup\nvariant = "variants.svg"; // [simple, advanced, pro]',
    annotations: [
      {
        text: '// svgradiogroup',
        tooltip:
          'Directive — next string variable becomes a single-select SVG group picker',
      },
      {
        text: 'variant',
        tooltip: 'Variable name — holds the currently selected group ID',
      },
      { text: '"variants.svg"', tooltip: 'SVG file to render as the selector' },
      {
        text: '[simple, advanced, pro]',
        tooltip: 'SVG group IDs to expose as selectable regions',
      },
    ],
    param: {
      name: 'variant',
      displayName: 'Variant',
      value: 'simple',
      defaultValue: 'simple',
      type: 'svgradiogroup',
      options: [
        { value: 'simple', label: 'simple' },
        { value: 'advanced', label: 'advanced' },
        { value: 'pro', label: 'pro' },
      ],
      svgFile: 'variants.svg',
      group: 'General',
    },
    blogSlug: 'svg-interactive-parameters',
  },

  {
    id: 'visibility-expr',
    title: 'Visibility Expressions',
    level: 'expert',
    dataType: 'special',
    uiSize: 'compact',
    origin: 'meshpit',
    description:
      'Append `{expr}` to a parameter comment to conditionally hide it. Supports `==`, `!=`, `&&`, `||`, `!`, and parentheses. Group headers accept the same syntax.',
    snippet: 'add_lid = true;\nlid_height = 8; // [3:20] {add_lid == true}',
    annotations: [
      {
        text: 'add_lid',
        tooltip:
          'Boolean guard parameter — controls visibility of the parameter below',
      },
      { text: 'lid_height', tooltip: 'Conditionally visible parameter' },
      { text: '[3:20]', tooltip: 'Range constraint for lid_height' },
      {
        text: '{add_lid == true}',
        tooltip:
          'Visibility expression — lid_height is only shown when add_lid is true',
      },
    ],
    param: {
      name: 'lid_height',
      displayName: 'Lid Height',
      value: 8,
      defaultValue: 8,
      type: 'number',
      range: { min: 3, max: 20 },
      visibilityExpr: 'add_lid == true',
      group: 'General',
    },
    blogSlug: 'smart-parameters',
  },

  {
    id: 'snapshots',
    title: 'Named Snapshots',
    level: 'intermediate',
    dataType: 'special',
    uiSize: 'compact',
    origin: 'meshpit',
    description:
      'A `/* [Snapshots] */` block defines named parameter presets. Each line is `Name: param=value, …`. The snapshot dropdown in the panel lets users jump between them instantly, and markdown action links (`[label](meshpit:snapshot:name)`) can trigger them in one click.',
    snippet:
      '/* [Snapshots]\nCompact: width=40, height=25, add_lid=false\nStandard: width=80, height=40, add_lid=true\n*/',
    annotations: [
      {
        text: '[Snapshots]',
        tooltip:
          'Block header — the parser scans for the first `/* [Snapshots] … */` block (case-insensitive)',
      },
      {
        text: 'Compact',
        tooltip:
          'Snapshot name — displayed in the dropdown and used as the ID in action links: `meshpit:snapshot:Compact`',
      },
      {
        text: 'width=40',
        tooltip:
          'Parameter override — bare numbers for numbers, quoted strings for strings and colours, e.g. color="#ff0000"',
      },
      {
        text: 'add_lid=false',
        tooltip: 'Boolean override — bare `true` or `false`, no quotes',
      },
    ],
    param: {
      name: 'width',
      displayName: 'Width',
      value: 40,
      defaultValue: 80,
      type: 'number',
      range: { min: 10, max: 150 },
      group: 'General',
    },
    blogSlug: 'snapshots',
  },

  {
    id: 'lock-annotation',
    title: 'Lock Annotation',
    level: 'expert',
    dataType: 'special',
    uiSize: 'compact',
    origin: 'meshpit',
    description:
      'Append `@lock` to a parameter comment to lock it by default — it is excluded from randomization until the user unlocks it via the UI. Apply `@lock` to a group header to lock every parameter in that group at once.',
    snippet:
      'width = 10; // [0:100] @lock\n\n/* [Dimensions] @lock */\ndepth = 20;',
    annotations: [
      {
        text: 'width',
        tooltip: 'Variable name — becomes the parameter label',
      },
      { text: '[0:100]', tooltip: 'Range constraint (optional)' },
      {
        text: '@lock',
        tooltip:
          'Lock annotation — parameter starts locked; user can unlock it via the UI',
      },
      {
        text: '/* [Dimensions] @lock */',
        tooltip: 'Group-level lock — all parameters in this group start locked',
      },
    ],
    param: {
      name: 'width',
      displayName: 'Width',
      value: 10,
      defaultValue: 10,
      type: 'number',
      range: { min: 0, max: 100 },
      locked: true,
      group: 'General',
    },
    blogSlug: 'smart-parameters',
  },

  {
    id: 'pubsub-annotation',
    title: 'Pub/Sub Annotation',
    level: 'expert',
    dataType: 'special',
    uiSize: 'compact',
    origin: 'meshpit',
    description:
      'Append `@pubsub` to a parameter comment to connect it to the inter-script pub/sub channel. The declaration is stripped before compilation — it has no effect in OpenSCAD — but its value is shared in real time with texture and shader scripts via `__subscribe__` / `__publish__`. Changing the parameter in the UI publishes to the channel; scripts publishing the same key update the UI value instantly.',
    snippet: 'hue = 0.5; // [0:0.01:1] @pubsub',
    annotations: [
      {
        text: 'hue',
        tooltip: 'Variable name — used as the pub/sub channel key',
      },
      { text: '[0:0.01:1]', tooltip: 'Range constraint for the UI slider (optional)' },
      {
        text: '@pubsub',
        tooltip:
          'Pub/sub annotation — value is shared with texture/shader scripts; stripped from compilation',
      },
    ],
    param: {
      name: 'hue',
      displayName: 'Hue',
      value: 0.5,
      defaultValue: 0.5,
      type: 'number',
      range: { min: 0, max: 1, step: 0.01 },
      pubsub: true,
      group: 'General',
    },
  },

  {
    id: 'code-expression',
    title: 'Code Expression',
    level: 'expert',
    dataType: 'special',
    uiSize: 'compact',
    origin: 'meshpit',
    description:
      'Declare a variable as a single-line OpenSCAD expression with `// [code]`. The value is stored as a quoted string in the source, but before compilation the declaration is replaced with a real expression assignment. This lets you use loop variables, math, and other parameters inside the expression — for example inside a `for` loop where `i` is in scope.',
    snippet: 'offset = "i * spacing"; // [code]',
    annotations: [
      {
        text: 'offset',
        tooltip: 'Variable name — wherever this is used in SCAD it receives the expression value',
      },
      {
        text: '"i * spacing"',
        tooltip: 'The OpenSCAD expression — any valid SCAD expression referencing other variables',
      },
      {
        text: '[code]',
        tooltip: 'Code annotation — tells the parameterizer to treat this as an expression, not a plain string',
      },
    ],
    param: {
      name: 'offset',
      displayName: 'Offset',
      value: 'i * spacing',
      defaultValue: 'i * spacing',
      type: 'code',
      group: 'General',
    },
  },

  {
    id: 'code-combobox',
    title: 'Code Expression with Presets',
    level: 'expert',
    dataType: 'special',
    uiSize: 'compact',
    origin: 'meshpit',
    description:
      'Add preset expressions to a `[code]` parameter using a `[val:Label, ...]` options list. A chevron button opens a dropdown of presets; the user can also type any custom expression directly. Labels are optional — omit the `:Label` part to use the expression itself as the label. Plain and labelled tokens can be mixed.',
    snippet: 'offset = "i * 2"; // [code] [i*2:Double, i*3:Triple, i/2:Half]',
    annotations: [
      {
        text: '"i * 2"',
        tooltip: 'Default expression — shown in the input on load',
      },
      {
        text: '[code]',
        tooltip: 'Code annotation — required to treat this as an expression parameter',
      },
      {
        text: '[i*2:Double, i*3:Triple, i/2:Half]',
        tooltip: 'Preset options list — each token is "expression:Label"; omit ":Label" to use the expression as the label',
      },
    ],
    param: {
      name: 'offset',
      displayName: 'Offset',
      value: 'i * 2',
      defaultValue: 'i * 2',
      type: 'code',
      group: 'General',
      options: [
        { value: 'i * 2', label: 'Double' },
        { value: 'i * 3', label: 'Triple' },
        { value: 'i / 2', label: 'Half' },
      ],
    },
  },
];
